import { useEffect, useState } from 'react';
import { useAnnouncements } from '@backstage-community/plugin-announcements-react';
import {
  Announcement,
  SIGNALS_CHANNEL_ANNOUNCEMENTS,
} from '@backstage-community/plugin-announcements-common';
import { useSignal } from '@backstage/plugin-signals-react';
import { IconButton, makeStyles, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import { Link } from '@backstage/core-components';
import { TOP_NAV_HEIGHT } from '../TopNav';

const useStyles = makeStyles(theme => ({
  banner: {
    position: 'fixed',
    top: TOP_NAV_HEIGHT + theme.spacing(1),
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'min(640px, calc(100% - 32px))',
    boxSizing: 'border-box',
    zIndex: theme.zIndex.appBar + 1,
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 4,
    boxShadow: theme.shadows[4],
  },
  content: {
    minWidth: 0,
    flex: 1,
    overflowWrap: 'anywhere',
  },
}));

export function isAnnouncementCurrent(announcement: Announcement, now: number) {
  const startsAt = Date.parse(announcement.start_at);
  const endsAt = announcement.until_date
    ? Date.parse(announcement.until_date)
    : Infinity;
  return announcement.active && startsAt <= now && now <= endsAt;
}

export const AnnouncementBanner = () => {
  const classes = useStyles();
  const { announcements, retry } = useAnnouncements({
    max: 5,
    active: true,
    current: true,
    sortBy: 'start_at',
    order: 'desc',
  });
  const [dismissed, setDismissed] = useState<string[]>([]);
  const { lastSignal } = useSignal(SIGNALS_CHANNEL_ANNOUNCEMENTS);

  useEffect(() => {
    if (lastSignal) retry();
  }, [lastSignal, retry]);

  useEffect(() => {
    const refresh = () => retry();
    const interval = window.setInterval(refresh, 60_000);
    window.addEventListener('focus', refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
    };
  }, [retry]);

  const announcement = announcements.results.find(
    item =>
      isAnnouncementCurrent(item, Date.now()) &&
      !dismissed.includes(`${item.id}:${item.updated_at}`),
  );
  if (!announcement) return null;

  return (
    <aside className={classes.banner} aria-label="Announcement">
      <AnnouncementIcon color="primary" />
      <div className={classes.content}>
        <Link to={`/announcements/view/${encodeURIComponent(announcement.id)}`}>
          {announcement.title}
        </Link>
        <Typography variant="body2">{announcement.excerpt}</Typography>
      </div>
      <IconButton
        size="small"
        aria-label="Dismiss announcement"
        onClick={() =>
          setDismissed(previous => [
            ...previous,
            `${announcement.id}:${announcement.updated_at}`,
          ])
        }
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </aside>
  );
};
